import { useState } from "react";
import { toast } from "sonner";
import { Drama, Send, Trophy, RotateCcw, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const PERSONAS = [
  { id: "fivem-owner", name: "Dane — FiveM Server Owner", desc: "300-player RP server. Skeptical, budget-conscious, burned by laggy MLOs before." },
  { id: "indie-founder", name: "Maya — Kickstarter Indie Founder", desc: "Just funded a UE5 survival game. Overwhelmed, deadline pressure, tiny team." },
  { id: "archviz-lead", name: "Rob — ArchViz Studio Lead", desc: "Corporate and procurement-driven. Comparing three vendors on paper this week." },
];

export default function SalesPitchSim() {
  const [phase, setPhase] = useState("idle"); // idle | chatting | scored
  const [persona, setPersona] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [personaName, setPersonaName] = useState("");
  const [messages, setMessages] = useState([]); // [{role, text}]
  const [draft, setDraft] = useState("");
  const [scorecard, setScorecard] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sales/ai/sim/start", { persona });
      setSessionId(r.data.id);
      setPersonaName(r.data.persona_name);
      setMessages([{ role: "buyer", text: r.data.buyer_message }]);
      setPhase("chatting");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    setMessages((m) => [...m, { role: "rep", text }]);
    setBusy(true);
    try {
      const r = await api.post(`/sales/ai/sim/${sessionId}/message`, { text });
      setMessages((m) => [...m, { role: "buyer", text: r.data.buyer_message }]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const end = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/sales/ai/sim/${sessionId}/end`);
      setScorecard(r.data.scorecard);
      setPhase("scored");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setPhase("idle"); setPersona(""); setSessionId(""); setMessages([]); setScorecard("");
  };

  if (phase === "idle") {
    return (
      <div data-testid="sim-setup" className="flex flex-col gap-3">
        <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">Pick a buyer persona and practice your pitch. They push back with real objections — the sales director scores you afterwards. 10 sessions per day.</p>
        {PERSONAS.map((p) => (
          <button key={p.id} data-testid={`sim-persona-${p.id}`} onClick={() => setPersona(p.id)} className={`text-left border px-4 py-3 transition-colors ${persona === p.id ? "border-nc-orange/60 bg-nc-orange/10" : "border-white/10 hover:border-white/30"}`}>
            <p className="font-sans font-bold text-xs text-zinc-100 uppercase">{p.name}</p>
            <p className="font-mono text-[9px] text-zinc-500 mt-1 leading-relaxed">{p.desc}</p>
          </button>
        ))}
        <button data-testid="sim-start-button" disabled={busy || !persona} onClick={start} className="self-start bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-5 py-2.5 flex items-center gap-2 transition-colors mt-1">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Drama className="w-3.5 h-3.5" />}
          {busy ? "Warming up…" : "Start Roleplay"}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase">Roleplay vs {personaName}</p>
        {phase === "chatting" && (
          <div className="flex gap-2">
            <button data-testid="sim-end-button" disabled={busy || messages.length < 3} onClick={end} className="font-mono text-[9px] tracking-[0.2em] uppercase px-3 py-2 bg-nc-green hover:bg-nc-green/80 disabled:opacity-40 text-black transition-colors flex items-center gap-1.5">
              <Trophy className="w-3 h-3" /> End & Score
            </button>
            <button data-testid="sim-abandon-button" onClick={reset} className="font-mono text-[9px] uppercase px-3 py-2 text-zinc-500 hover:text-zinc-200 transition-colors flex items-center gap-1.5">
              <RotateCcw className="w-3 h-3" /> Abandon
            </button>
          </div>
        )}
      </div>

      <div data-testid="sim-chat" className="border border-white/10 bg-[#050505]/70 p-4 flex flex-col gap-3 max-h-[420px] overflow-y-auto">
        {messages.map((m, i) => (
          <div key={i} data-testid={`sim-message-${i + 1}`} className={`max-w-[85%] px-3.5 py-2.5 ${m.role === "buyer" ? "self-start border border-nc-orange/30 bg-nc-orange/5" : "self-end border border-nc-blue/30 bg-nc-blue/5"}`}>
            <p className={`font-mono text-[8px] tracking-[0.2em] uppercase mb-1 ${m.role === "buyer" ? "text-nc-orange" : "text-nc-blue"}`}>{m.role === "buyer" ? personaName.split("—")[0].trim() : "You"}</p>
            <p className="font-mono text-[11px] text-zinc-200 leading-relaxed whitespace-pre-wrap">{m.text}</p>
          </div>
        ))}
        {busy && phase === "chatting" && <p className="font-mono text-[10px] text-zinc-600 animate-pulse self-start">{personaName.split("—")[0].trim()} is typing…</p>}
      </div>

      {phase === "chatting" && (
        <div className="flex gap-2">
          <input
            data-testid="sim-input"
            className="flex-1 bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-[11px] text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-orange focus:border-nc-orange"
            placeholder="Reply to the buyer…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !busy) send(); }}
            maxLength={2000}
          />
          <button data-testid="sim-send" disabled={busy || !draft.trim()} onClick={send} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black px-4 flex items-center gap-2 transition-colors">
            <Send className="w-4 h-4" />
          </button>
        </div>
      )}

      {phase === "scored" && (
        <div className="flex flex-col gap-3">
          <div className="border border-nc-green/40 bg-nc-green/5">
            <div className="border-b border-white/10 px-4 py-2.5 font-mono text-[9px] tracking-[0.3em] text-nc-green uppercase flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5" /> Sales Director Scorecard
            </div>
            <pre data-testid="sim-scorecard" className="font-mono text-[11px] text-zinc-200 leading-relaxed whitespace-pre-wrap p-4">{scorecard}</pre>
          </div>
          <button data-testid="sim-again-button" onClick={reset} className="self-start font-mono text-[9px] tracking-[0.2em] uppercase px-4 py-2.5 border border-nc-orange/40 text-nc-orange hover:bg-nc-orange/10 transition-colors flex items-center gap-2">
            <RotateCcw className="w-3.5 h-3.5" /> Practice Again
          </button>
        </div>
      )}
    </div>
  );
}
